import { Controller } from "@hotwired/stimulus"
import { logger } from "../utilities/logger"

// Data Table Per-Page Controller
// Handles the per-page selector dropdown in data table info display
// Saves user preference and triggers table reload
//
// Usage:
//   select[data-controller="data-table-per-page"
//          data-data-table-per-page-controller-name-value="program_operators"
//          data-data-table-per-page-total-value="150"
//          data-action="change->data-table-per-page#change"]
//
export default class extends Controller {
  static values = {
    controllerName: String,  // e.g., "program_operators" - used for settings key
    total: Number            // Total records count (for "All" option)
  }

  connect() {
    logger.log("DataTablePerPage controller connected")
    logger.log("Controller name:", this.controllerNameValue)
    logger.log("Total records:", this.totalValue)
  }

  // Handle per-page selection change
  async change(event) {
    const selectedValue = event.target.value
    logger.log("Per-page changed to:", selectedValue)

    // Get parent data-table controller
    const dataTableController = this.getDataTableController()
    if (!dataTableController) {
      logger.error("Could not find parent data-table controller")
      return
    }

    // Update page size in data table controller
    // Convert "all" to total count and set showAll flag, otherwise parse as integer
    if (selectedValue === 'all') {
      dataTableController.pageSizeValue = this.totalValue
      dataTableController.showAll = true
      logger.log("Show all mode enabled")
    } else {
      dataTableController.pageSizeValue = parseInt(selectedValue)
      dataTableController.showAll = false
    }

    // Reset to first page when changing page size
    dataTableController.pageValue = 0

    // Save preference to backend (save "all" as-is, backend will convert to -1)
    await this.savePreference(selectedValue)

    // Reload data with new page size
    dataTableController.loadData()
  }

  // Save user preference to backend
  async savePreference(perPageValue) {
    const csrfToken = document.querySelector('[name="csrf-token"]')?.content

    if (!csrfToken) {
      logger.warn('CSRF token not found, cannot save per-page preference')
      return
    }

    try {
      const response = await fetch('/users/update_data_table_per_page', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({
          controller_name: this.controllerNameValue,
          per_page: perPageValue
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      logger.log('Per-page preference saved successfully')
    } catch (error) {
      logger.error('Failed to save per-page preference:', error)
    }
  }

  // Find parent data-table controller
  getDataTableController() {
    // The per-page select is rendered inside the info target, which is inside the data-table-container
    // Walk up the DOM tree to find the element with .data-table-container class
    let element = this.element
    while (element) {
      if (element.classList.contains('data-table-container')) {
        // Get the Stimulus controller instance from this element
        const app = this.application
        return app.getControllerForElementAndIdentifier(element, 'data-table')
      }
      element = element.parentElement
    }
    return null
  }
}
